/**
 * Call-lifecycle — één finalizer voor het SDK-pad én het Realtime-pad.
 *
 * Vóór deze module hadden beide einde-paden hun eigen (asymmetrische) cleanup:
 * het Realtime-terminal-pad sloeg sampling stoppen, audio loskoppelen, de
 * mute-reset en de dispositie-capture over. finalizeCall() is nu de enige weg
 * naar "gesprek voorbij"; client.ts hangt zijn owner-state (dispositie, stats,
 * mute) via `setOnFinalized` aan.
 *
 * De registry-opruiming zelf gebeurt niet hier maar centraal in
 * call-registry.ts (done-subscription op de actor).
 */

import * as registry from './call-registry';
import { stopSampling } from './telemetry';
import { clearRemoteAudio } from './audio';
import { closeFor } from './notifications';
import { emit } from './events';
import type { CallRecord, ClientCallId, EventSource } from './types';

/** Telnyx SDK Call-objecten per clientCallId (instances, geen records). */
export const sdkCalls = new Map<ClientCallId, unknown>();

export interface FinalizedInfo {
  record: CallRecord | null;
  answered: boolean;
  noSdkCallsLeft: boolean;
  /** finalizeCall-reden: SDK 'hangup'/'destroy' of Realtime-status. */
  reason: string;
  source: EventSource;
  endedAt: number;
}

let onFinalized: ((info: FinalizedInfo) => void) | null = null;

/** client.ts registreert hier zijn owner-state-afhandeling (dispositie, mute, stats). */
export function setOnFinalized(cb: ((info: FinalizedInfo) => void) | null): void {
  onFinalized = cb;
}

const finalized = new Set<ClientCallId>();

/**
 * Beëindig een gesprek: audio los, notificatie dicht, sampling stoppen,
 * SDK-referentie weg, machine → ended, destroy-event. Idempotent: het pad dat
 * als tweede aankomt (SDK vs Realtime) is een no-op.
 */
export function finalizeCall(
  clientCallId: ClientCallId,
  reason: string,
  source: EventSource,
  /** Extra destroy-metadata, bv. de SDK-ophangoorzaak (cause/sipCode). */
  extra?: Record<string, unknown>,
): void {
  if (finalized.has(clientCallId)) return;
  finalized.add(clientCallId);
  // Bounded geheugen: de set hoeft dubbele finalisatie alleen rond het
  // call-einde te vangen.
  setTimeout(() => finalized.delete(clientCallId), 60_000);

  const entry = registry.get(clientCallId);
  // Snapshot vóór de DESTROYED-transitie: de dispositie-UI heeft het record
  // nodig nadat de registry-entry is opgeruimd.
  const record = entry ? { ...entry.record } : null;
  const callId = record?.callId ?? null;
  const answeredAt = record?.answeredAt ?? null;

  // Het remote-audio-element is gedeeld over alle gesprekken. Alleen wissen
  // als er geen ánder levend gesprek meer is: het einde van een tweede,
  // onbeantwoorde call (ring-timeout van de ring group tijdens een actief
  // gesprek) trok anders de audio onder het lopende gesprek vandaan —
  // incident 2026-07-06, gesprek leek "verbroken" maar liep nog.
  const otherLive = registry
    .all()
    .some(
      (e) =>
        e.record.clientCallId !== clientCallId &&
        (e.record.state === 'active' || e.record.answeredAt !== null),
    );
  if (!otherLive) clearRemoteAudio();
  closeFor(clientCallId);
  void stopSampling(clientCallId, callId);
  sdkCalls.delete(clientCallId);

  if (answeredAt) {
    emit({ type: 'recording_stopped', source: 'client', clientCallId });
  }

  registry.send(clientCallId, { type: 'DESTROYED', reason });

  const endedAt = Date.now();
  const durationMs = answeredAt ? endedAt - answeredAt.getTime() : null;
  emit({
    type: 'destroy',
    source,
    clientCallId,
    metadata: {
      reason,
      durationMs,
      answeredAt: answeredAt ? answeredAt.toISOString() : null,
      ...(extra ?? {}),
    },
  });

  onFinalized?.({
    record,
    answered: Boolean(answeredAt),
    noSdkCallsLeft: sdkCalls.size === 0,
    reason,
    source,
    endedAt,
  });
}

/** Volledige reset bij teardown/resign (geen events, geen dispositie). */
export function resetLifecycle(): void {
  sdkCalls.clear();
  finalized.clear();
}
